<template>
	<div class="relative" ref="wrapper">
		<!-- Toggle button - matches Bulk button styling -->
		<button
			@click.stop="toggle"
			class="rounded-full border border-black/10 px-4 py-1.5 text-xs font-semibold tracking-wide uppercase shadow-sm transition-colors flex items-center gap-1.5"
			:class="isOpen ? 'bg-black text-white' : 'bg-white/90 hover:bg-black hover:text-white'"
		>
			Maps
			<svg 
				xmlns="http://www.w3.org/2000/svg" width="8" height="8" viewBox="0 0 24 24" 
				fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round"
				class="transition-transform duration-200" :class="{ 'rotate-180': isOpen }"
			>
				<polyline points="6 9 12 15 18 9"/>
			</svg>
		</button>

		<!-- Dropdown panel -->
		<div 
			v-show="isOpen"
			class="absolute top-full right-0 mt-1.5 w-56 bg-white rounded-md shadow-lg border border-black/10 overflow-hidden z-50"
		>
			<div v-if="loading" class="px-3 py-2.5 text-xs text-[#999]">Loading maps...</div>
			<template v-else>
				<div
					v-for="map in (maps || [])"
					:key="map.id"
					@click.stop="selectMap(map)"
					class="flex items-center gap-2.5 px-3 py-2 cursor-pointer transition-colors border-b border-[#F0F0F0] last:border-b-0"
					:class="[
						getRowClass(map),
						settingActive ? 'pointer-events-none opacity-60' : ''
					]"
				>
					<!-- Status dot -->
					<div 
						class="w-1.5 h-1.5 rounded-full flex-shrink-0"
						:class="getDotClass(map)"
					></div>

					<!-- Title + badges -->
					<div class="flex-1 min-w-0">
						<div class="text-xs font-medium text-black truncate leading-tight">{{ map.title }}</div>
						<div v-if="!map.enabled && map.status !== 'pending'" class="text-[0.6rem] text-[#999] uppercase tracking-wide leading-tight">Draft</div>
						<div v-if="map.status === 'pending' && map.postDate" class="text-[0.6rem] text-[#ff9100] uppercase tracking-wide leading-tight">
							Scheduled {{ formatDate(map.postDate) }}
						</div>
						<div v-else-if="map.status === 'pending'" class="text-[0.6rem] text-[#ff9100] uppercase tracking-wide leading-tight">Pending</div>
					</div>

					<!-- Status labels -->
					<div v-if="settingActive && String(previewingId) === String(map.id)" class="flex-shrink-0">
						<svg class="animate-spin h-3 w-3 text-[#767676]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
							<circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/>
							<path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
						</svg>
					</div>
					<div 
						v-else-if="String(map.id) === String(activeId)" 
						class="text-[0.6rem] uppercase tracking-wider font-semibold text-[#0DB72D] flex-shrink-0"
					>Active</div>
					<div 
						v-else-if="previewingId && String(map.id) === String(previewingId)" 
						class="text-[0.6rem] uppercase tracking-wider font-semibold text-[#2563EB] flex-shrink-0"
					>Previewing</div>
					<div 
						v-else-if="map.status === 'pending'" 
						class="w-2 h-2 rounded-full flex-shrink-0 bg-[#ff910070]"
					></div>
				</div>

				<!-- Set Active / Cancel buttons - only when previewing a non-active map -->
				<div 
					v-if="previewingId && String(previewingId) !== String(activeId)"
					class="border-t border-[#E0E0E0] px-3 py-2 flex items-center gap-2"
				>
					<button
						@click.stop="confirmSetActive"
						:disabled="settingActive"
						class="flex-1 rounded-full bg-[#0C0C0C] text-white text-[0.65rem] uppercase tracking-wider font-semibold py-1.5 px-3 hover:bg-black/80 transition-colors disabled:opacity-50 text-center"
					>
						{{ settingActive ? 'Setting...' : 'Set Active' }}
					</button>
					<button
						@click.stop="cancelPreview"
						:disabled="settingActive"
						class="rounded-full bg-[#E6E6E6] text-black text-[0.65rem] uppercase tracking-wider font-semibold py-1.5 px-3 hover:bg-[#D0D0D0] transition-colors disabled:opacity-50"
					>
						Cancel
					</button>
				</div>

				<div v-if="!maps || maps.length === 0" class="px-3 py-2.5 text-xs text-[#999]">No maps found</div>
			</template>
		</div>
	</div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'

export default {
	name: 'MapSwitcher',
	props: {
		maps: { type: Array, default: () => [] },
		activeId: { type: [String, Number], default: null },
		loading: { type: Boolean, default: false }
	},
	emits: ['preview', 'set-active', 'cancel-preview'],
	setup(props, { emit }) {
		const wrapper = ref(null)
		const isOpen = ref(false)
		const previewingId = ref(null)
		const settingActive = ref(false)

		const toggle = () => {
			isOpen.value = !isOpen.value
		}

		const selectMap = (map) => {
			// Clicking the active map while not previewing → just close
			if (String(map.id) === String(props.activeId) && !previewingId.value) {
				isOpen.value = false
				return
			}

			// Clicking the active map while previewing → cancel preview
			if (String(map.id) === String(props.activeId) && previewingId.value) {
				cancelPreview()
				return
			}

			// Clicking the already-previewing map → do nothing
			if (previewingId.value && String(map.id) === String(previewingId.value)) {
				return
			}

			// Preview this map (just swap SVG locally, no backend call)
			previewingId.value = map.id
			emit('preview', map)
		}

		const formatDate = (dateStr) => {
			if (!dateStr) return ''
			try {
				const d = new Date(dateStr)
				return isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, {month: 'short', day: 'numeric'})
			} catch {
				return ''
			}
		}

		const confirmSetActive = () => {
			if (!previewingId.value) return
			const map = props.maps?.find(m => String(m.id) === String(previewingId.value))
			if (!map) return
			settingActive.value = true
			emit('set-active', map)
		}

		const cancelPreview = () => {
			previewingId.value = null
			emit('cancel-preview')
		}

		// Parent calls after set-active completes
		const resetAfterSetActive = (success) => {
			settingActive.value = false
			if (success) {
				previewingId.value = null
				isOpen.value = false
			}
		}

		// Parent calls after cancel-preview reload completes
		const resetPreview = () => {
			previewingId.value = null
		}

		const getRowClass = (map) => {
			if (previewingId.value && String(map.id) === String(previewingId.value)) return 'bg-[#EFF6FF]'
			if (String(map.id) === String(props.activeId)) return 'bg-[#F0FFF4]'
			return 'hover:bg-[#F7F7F7]'
		}

		const getDotClass = (map) => {
			if (previewingId.value && String(map.id) === String(previewingId.value)) return 'bg-[#2563EB]'
			if (String(map.id) === String(props.activeId)) return 'bg-[#0DB72D]'
			return 'bg-transparent'
		}

		// Close on outside click
		const handleClickOutside = (e) => {
			if (wrapper.value && !wrapper.value.contains(e.target)) {
				isOpen.value = false
			}
		}

		onMounted(() => document.addEventListener('click', handleClickOutside))
		onUnmounted(() => document.removeEventListener('click', handleClickOutside))

		return { 
			wrapper, isOpen, previewingId, settingActive,
			toggle, selectMap, confirmSetActive, cancelPreview, formatDate,
			resetAfterSetActive, resetPreview,
			getRowClass, getDotClass
		}
	}
}
</script>